import React, { useState } from "react";
import { Col, Stack } from "react-bootstrap";
import { FiatCurrency } from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./HowMuchWouldYouLikeToMake.scss";
import OnboardingHeader from "../../../components/Headers/OnboardingHeader/OnboardingHeader";
import { OnboardingSteps } from "../../../utils/constants";
import { formatCurrency } from "../../../utils/helpers";
import { setStoredSelectedEarnings } from "../../../utils/storage";
import { CustomSpinner } from "../../../components/CustomSpinner/CustomSpinner";
import useOnboardingSteps from "../../../hooks/useOnboardingSteps";
import useExchangeRatesQuery from "../../../react-query/queries/use-exchange-rates";
import useAnalytics from "../../../hooks/useAnalytics";
import HyperCoin from "../../../components/HyperCoin/HyperCoin";
import LightBulbIcon from "../../../components/icons/LightBulbIcon";
import PrimaryActionButton from "../../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import CurrencySelectorDropdown from "../../../components/onboarding/CurrencySelectorDropdown/CurrencySelectorDropdown";
import OnboardingContainer from "../../../components/onboarding/OnboardingContainer/OnboardingContainer";

type HowMuchToMakeSelectionItemProps = {
  hyperAmount: string;
  amount: string;
  selected: string;
  setSelected: (value: string) => void;
};

function HowMuchToMakeSelectionItem({
  hyperAmount,
  amount,
  selected,
  setSelected,
}: HowMuchToMakeSelectionItemProps) {
  const isSelected = selected === hyperAmount;
  const buttonStyles: React.CSSProperties = {
    borderColor: isSelected ? "#6C53E8" : "#352B4D",
    boxShadow: isSelected ? "0px 8px 16px 0px #00000069" : undefined,
  };

  return (
    <button
      className="m-0 how-much-to-make-item"
      onClick={() => setSelected(hyperAmount)}
      style={buttonStyles}
      aria-pressed={isSelected}
    >
      <p className="d-flex align-items-center gap-1 text-white hyper-amount-to-make">
        <HyperCoin className="hyper-coin" />
        <span className="hpr-amount">{formatCurrency(+hyperAmount)}</span>
        <span className="frequency">daily</span>
      </p>

      <p className="text-white fiat-amount">{amount}</p>
    </button>
  );
}

const HPR_EARNING_GOALS = [1000, 5000, 10000, 20000];

function HowMuchWouldYouLikeToMake() {
  const analytics = useAnalytics();
  const [selectedEarning, setSelectedEarning] = useState<string>("");
  const [selectedCurrency, setSelectedCurrency] = useState<FiatCurrency>();
  const { goToStep } = useOnboardingSteps();
  const { getExchangeRate } = useExchangeRatesQuery();
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });

  const handleSetGoal = () => {
    setStoredSelectedEarnings(+selectedEarning);
    analytics.sendChooseRewardSelectionEvent(+selectedEarning);
    goToStep(OnboardingSteps.SETTING_EARNINGS_GOALS);
  };

  return (
    <>
      <OnboardingContainer className="MobileOnly position-relative d-flex flex-column gap-4 how-much-would-you-like-to-make-page">
        <Col className="how-much-would-you-like-to-make-header">
          <OnboardingHeader amountActive={1} />
        </Col>

        <div className="d-flex flex-column justify-content-between flex-grow-1">
          <Stack
            gap={4}
            className="how-much-would-you-like-to-make-page-content"
          >
            <h1 className="earnings-target-heading">
              {t("how_much_do_you_want_to_win_daily")}?
            </h1>
            {!selectedCurrency && <CustomSpinner className="text-white" />}
            <Stack
              gap={2}
              className="how-much-would-you-like-to-make-selection-grid"
            >
              <CurrencySelectorDropdown
                selectedCurrency={selectedCurrency}
                setSelectedCurrency={setSelectedCurrency}
              />

              {selectedCurrency && (
                <Stack
                  gap={3}
                  className="how-much-would-you-like-to-make-selection-options"
                >
                  {selectedCurrency === FiatCurrency.NGN &&
                    HPR_EARNING_GOALS.map((goal) => (
                      <HowMuchToMakeSelectionItem
                        key={goal}
                        hyperAmount={goal.toString()}
                        amount={formatCurrency(
                          goal * (getExchangeRate(FiatCurrency.NGN) ?? 0),
                          FiatCurrency.NGN
                        )}
                        selected={selectedEarning}
                        setSelected={setSelectedEarning}
                      />
                    ))}
                  {selectedCurrency === FiatCurrency.USD &&
                    HPR_EARNING_GOALS.map((goal) => (
                      <HowMuchToMakeSelectionItem
                        key={goal}
                        hyperAmount={goal.toString()}
                        amount={formatCurrency(
                          Math.floor(
                            goal * (getExchangeRate(FiatCurrency.USD) ?? 0)
                          ),
                          FiatCurrency.USD
                        )}
                        selected={selectedEarning}
                        setSelected={setSelectedEarning}
                      />
                    ))}
                  <Stack
                    direction="horizontal"
                    gap={2}
                    className="hpr-definition-container"
                  >
                    <LightBulbIcon fill="white" width={12} />
                    <p className="hpr-definition">
                      HPR (<HyperCoin />) {t("hpr_definition")}.
                    </p>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Stack>

          <PrimaryActionButton
            size="lg"
            text={t("set_my_goal")}
            disabled={!selectedEarning}
            onClick={handleSetGoal}
          />
        </div>
      </OnboardingContainer>
    </>
  );
}

export default HowMuchWouldYouLikeToMake;
